import React from 'react';
import { useTranslation } from 'react-i18next';
import { BriefcaseBusiness, Building2, Quote } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from './ui/carousel';

const TestimonialsSectionEnhanced = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const testimonials = [
    {
      id: 1,
      roleAr: 'مدير تنفيذي – شركة تجارية',
      roleEn: 'Executive Director – Commercial Company',
      quoteAr: 'تعاملنا مع وازن في مسألة تعاقدية حساسة، وتمت المعالجة باحترافية عالية ووضوح كامل في الإجراءات.',
      quoteEn: 'We worked with Wazin on a sensitive contractual matter, and it was handled with high professionalism and complete procedural clarity.',
      icon: Building2
    },
    {
      id: 2,
      roleAr: 'مدير قانوني – قطاع صناعي',
      roleEn: 'Legal Director – Industrial Sector',
      quoteAr: 'فريق وازن قدّم دعماً قانونياً منظماً وسريعاً، مع متابعة دقيقة لكافة تفاصيل العمل.',
      quoteEn: 'Wazin’s team provided organized and prompt legal support, with careful follow-up on every detail of the matter.',
      icon: BriefcaseBusiness
    },
    {
      id: 3,
      roleAr: 'مالك منشأة',
      roleEn: 'Business Owner',
      quoteAr: 'التجربة مع وازن كانت واضحة وسلسة، مع التزام كامل بالمواعيد وجودة في التنفيذ.',
      quoteEn: 'The experience with Wazin was clear and smooth, with full commitment to timelines and quality in execution.',
      icon: Building2
    },
    {
      id: 4,
      roleAr: 'شركة في قطاع الأعمال',
      roleEn: 'Business Sector Company',
      quoteAr: 'تم التعامل مع الملف القانوني بكفاءة عالية، مع تقديم حلول عملية قابلة للتطبيق.',
      quoteEn: 'The legal file was handled with high efficiency, with practical and applicable solutions throughout.',
      icon: BriefcaseBusiness
    }
  ];

  return (
    <section id="testimonials" className="py-24 px-4 bg-gradient-to-b from-white via-[#faf8f3] to-white relative z-10 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14" dir={isArabic ? 'rtl' : 'ltr'}>
          <h2 className="text-4xl sm:text-5xl font-bold text-wazin-navy mb-5">
            {isArabic ? 'تجارب عمل مع وازن' : 'Working Experiences with Wazin'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {isArabic
              ? 'نماذج مختصرة من تجارب عملائنا في مختلف القطاعات.'
              : 'Brief examples of our clients’ experiences across different sectors.'}
          </p>
        </div>

        <div className="relative px-12 md:px-16 lg:px-20" dir={isArabic ? 'rtl' : 'ltr'}>
          <Carousel
            opts={{
              align: 'center',
              loop: true,
              direction: isArabic ? 'rtl' : 'ltr'
            }}
            className="w-full"
          >
            <CarouselContent>
              {testimonials.map((testimonial) => {
                const Icon = testimonial.icon;

                return (
                  <CarouselItem key={testimonial.id} className="basis-full">
                    <div className="flex justify-center">
                      <div className="w-full max-w-4xl rounded-[32px] bg-white border border-[#A27849]/20 shadow-[0_18px_50px_rgba(15,23,42,0.08)] p-8 md:p-12">
                        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8" dir={isArabic ? 'rtl' : 'ltr'}>
                          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#B58C5A] via-[#A27849] to-[#8C653A] flex items-center justify-center shadow-lg flex-shrink-0">
                            <Icon className="w-9 h-9 text-white" />
                          </div>

                          <div className={`flex-1 ${isArabic ? 'text-right' : 'text-left'}`}>
                            <div className="flex items-center gap-3 mb-4 text-[#8C653A]">
                              <Quote className="w-5 h-5" />
                              <span className="text-sm font-bold tracking-[0.2em]">
                                {isArabic ? 'تجربة عمل' : 'WORKING NOTE'}
                              </span>
                            </div>

                            <h3 className="text-2xl md:text-3xl font-bold text-wazin-navy mb-4">
                              {isArabic ? testimonial.roleAr : testimonial.roleEn}
                            </h3>

                            <p className="text-lg md:text-xl text-gray-700 leading-9 max-w-3xl">
                              "{isArabic ? testimonial.quoteAr : testimonial.quoteEn}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            <CarouselPrevious className="left-0 md:left-1 h-11 w-11 border-[#A27849]/35 bg-white text-[#8C653A] shadow-[0_10px_25px_rgba(15,23,42,0.08)] hover:bg-[#A27849] hover:text-white disabled:opacity-35" />
            <CarouselNext className="right-0 md:right-1 h-11 w-11 border-[#A27849]/35 bg-white text-[#8C653A] shadow-[0_10px_25px_rgba(15,23,42,0.08)] hover:bg-[#A27849] hover:text-white disabled:opacity-35" />
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSectionEnhanced;
